import { useEffect, type ReactNode } from 'react'
import { InteractionStatus } from '@azure/msal-browser'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { loginRequest } from '@/features/auth/authConfig'
import { useBootstrapUser } from '@/hooks/useBootstrapUser'

interface Props {
  children: ReactNode
}

const DISABLE_AUTH = import.meta.env.VITE_DISABLE_AUTH === 'true'

/** Matches Blazor spinner.css .loader — shown during auth/bootstrap. */
function PageLoader() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-6">
      <div className="du-loader" aria-hidden="true" />
      <span className="text-sm text-[#53565a]">Loading...</span>
    </div>
  )
}

/**
 * MsalProtectedRoute.tsx
 * MSAL-dependent auth flow.
 * All MSAL hooks are called unconditionally at the top level.
 * This component is only rendered when DISABLE_AUTH=false.
 */
function MsalProtectedRoute({ children }: Props) {
  const isAuthenticated = useIsAuthenticated()
  const { instance, inProgress } = useMsal()
  const { isBootstrapping } = useBootstrapUser()

  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      void instance.loginRedirect(loginRequest)
    }
  }, [instance, isAuthenticated, inProgress])

  if (inProgress !== InteractionStatus.None || isBootstrapping) {
    return <PageLoader />
  }

  if (!isAuthenticated) {
    return <PageLoader />
  }

  return <>{children}</>
}

/**
 * ProtectedRoute.tsx
 * Guards authenticated routes.
 *
 * When VITE_DISABLE_AUTH=true: skips MSAL entirely, renders children directly
 * after useBootstrapUser populates the store with mock data (API key auth).
 *
 * When VITE_DISABLE_AUTH=false: standard MSAL flow (bearer token auth) via MsalProtectedRoute.
 */
export function ProtectedRoute({ children }: Props) {
  const { isBootstrapping } = useBootstrapUser()

  // ── Auth-disabled path (localhost with API key) ────────────────────────────
  if (DISABLE_AUTH) {
    if (isBootstrapping) {
      return <PageLoader />
    }
    return <>{children}</>
  }

  // ── MSAL auth path (production with bearer token) ──────────────────────────
  // Render a different component that calls MSAL hooks unconditionally
  return <MsalProtectedRoute>{children}</MsalProtectedRoute>
}

export default ProtectedRoute

